import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PiWarningCircleLight, PiArrowLeftLight, PiArrowRightLight, PiCalendarCheckLight, PiCheckCircleLight, PiCaretRightLight, PiCircleNotchLight, PiEnvelopeSimpleLight, PiMapPinLight, PiChatCircleLight, PiPhoneLight, PiMagnifyingGlassLight, PiUserLight, PiUsersLight, PiVideoCameraLight } from 'react-icons/pi';
import { candidateAPI, interviewAPI } from '../../services/api';
import { formatCompensationRange } from '../../utils/compensation';

const inputClass = 'h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100';
const textareaClass = 'min-h-[96px] w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm leading-6 text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100';

const labelize = (value) => (
  String(value || 'Not set')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const extractError = (error, fallback) => (
  error?.response?.error ||
  error?.response?.message ||
  error?.message ||
  error?.error ||
  fallback
);

const initialsFor = (name) => (
  String(name || 'Candidate')
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
);

const formatMoney = (salary) => formatCompensationRange(salary);

const isValidEmail = (value) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(value || '').trim());

const isValidUrl = (value) => {
  try {
    const parsed = new URL(value);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch (_) {
    return false;
  }
};

const interviewTypes = [
  { value: 'video_call', label: 'Video', icon: PiVideoCameraLight },
  { value: 'phone', label: 'Phone', icon: PiPhoneLight },
  { value: 'in_person', label: 'In person', icon: PiMapPinLight }
];

const Field = ({ label, children, error }) => (
  <label className="block min-w-0">
    <span className="mb-1.5 block text-xs font-medium uppercase tracking-[0.12em] text-slate-500">{label}</span>
    {children}
    {error && <span className="mt-1.5 block text-xs font-medium text-rose-600">{error}</span>}
  </label>
);

const Panel = ({ title, icon: Icon, children }) => (
  <section className="rounded-lg border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
    <div className="border-b border-slate-100 px-4 py-3 sm:px-5">
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex items-center justify-center text-sky-600">
          <Icon size={21} />
        </span>
        <h2 className="truncate font-display text-[17px] font-normal text-[#0b1f3a]">{title}</h2>
      </div>
    </div>
    <div className="p-4 sm:p-5">{children}</div>
  </section>
);

const CandidateOption = ({ match, selected, onSelect }) => {
  const candidate = match.candidate || {};
  const name = candidate.fullName || 'Candidate';
  const salary = formatMoney(candidate.salaryExpectations);

  return (
    <button
      type="button"
      onClick={() => onSelect(match.matchId)}
      className={`w-full rounded-lg border p-3 text-left transition ${selected ? 'border-sky-300 bg-sky-50' : 'border-slate-200 bg-white hover:border-sky-200'}`}
    >
      <div className="flex items-start gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-xs font-medium ${selected ? 'bg-sky-600 text-white' : 'bg-[#eef9ff] text-sky-700'}`}>
          {initialsFor(name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-[#0b1f3a]">{name}</div>
              <div className="mt-1 truncate text-xs text-slate-500">{match.job?.title || 'No role'}</div>
            </div>
            {selected && <PiCheckCircleLight size={14} className="shrink-0 text-sky-700" />}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
            {candidate.location && <span className="inline-flex items-center gap-1"><PiMapPinLight size={12} /> {candidate.location}</span>}
            {candidate.experienceYears !== null && candidate.experienceYears !== undefined && <span>{candidate.experienceYears} yrs</span>}
            {salary && <span>{salary}</span>}
            <span>Band {match.matchBand} · {Math.round(Number(match.overallMatchScore || 0))}%</span>
          </div>
        </div>
      </div>
    </button>
  );
};

const ScheduleInterviewPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const queryMatchId = query.get('matchId') || '';
  const [matches, setMatches] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingMatches, setLoadingMatches] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    matchId: queryMatchId,
    interviewTitle: 'Hiring team interview',
    interviewType: 'video_call',
    platform: 'Google Meet',
    meetingLink: '',
    interviewLocation: '',
    date: '',
    time: '',
    durationMinutes: 60,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    interviewerEmails: '',
    ccEmails: '',
    candidateMessage: '',
    instructions: '',
    notes: ''
  });

  const loadMatches = useCallback(async () => {
    setLoadingMatches(true);
    try {
      const response = await candidateAPI.getEmployerCandidates({
        matchStatus: 'interview_eligible',
        minScore: 0,
        limit: 100,
        sortBy: 'updated_at'
      });
      const nextMatches = Array.isArray(response.candidates) ? response.candidates : [];
      setMatches(nextMatches);
      setForm((current) => (
        !current.matchId && nextMatches.length
          ? { ...current, matchId: String(nextMatches[0].matchId) }
          : current
      ));
    } catch (error) {
      toast.error(extractError(error, 'Failed to load interview-eligible candidates'));
    } finally {
      setLoadingMatches(false);
    }
  }, []);

  useEffect(() => {
    loadMatches();
  }, [loadMatches]);

  const selectedMatch = useMemo(() => (
    matches.find((match) => String(match.matchId) === String(form.matchId)) || null
  ), [form.matchId, matches]);

  const filteredMatches = useMemo(() => {
    const queryText = search.trim().toLowerCase();
    if (!queryText) return matches;
    return matches.filter((match) => [
      match.candidate?.fullName,
      match.candidate?.location,
      match.job?.title,
      match.job?.location,
      ...(match.candidate?.skills || []).map((skill) => skill.name || skill.skill_name || skill)
    ].filter(Boolean).some((value) => String(value).toLowerCase().includes(queryText)));
  }, [matches, search]);

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: '' }));
  };

  const validate = () => {
    const nextErrors = {};
    const interviewers = emailList(form.interviewerEmails);
    const ccList = emailList(form.ccEmails);
    if (!selectedMatch) nextErrors.matchId = 'Select an interview-eligible candidate.';
    if (!form.interviewTitle.trim()) nextErrors.interviewTitle = 'Interview title is required.';
    if (!form.date) nextErrors.date = 'Date is required.';
    if (!form.time) nextErrors.time = 'Time is required.';
    if (form.interviewType === 'video_call' && !form.meetingLink.trim()) {
      nextErrors.meetingLink = 'Meeting link is required for video interviews.';
    } else if (form.meetingLink.trim() && !isValidUrl(form.meetingLink.trim())) {
      nextErrors.meetingLink = 'Enter a valid http or https meeting link.';
    }
    if (form.interviewType === 'in_person' && !form.interviewLocation.trim()) nextErrors.interviewLocation = 'Location is required for in-person interviews.';
    if (!interviewers.length) {
      nextErrors.interviewerEmails = 'At least one interviewer email is required.';
    } else if (interviewers.some((email) => !isValidEmail(email))) {
      nextErrors.interviewerEmails = 'Enter valid comma-separated email addresses.';
    }
    if (ccList.some((email) => !isValidEmail(email))) nextErrors.ccEmails = 'Enter valid comma-separated email addresses.';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const emailList = (value) => value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    const scheduledDate = new Date(`${form.date}T${form.time}`);
    if (Number.isNaN(scheduledDate.getTime()) || scheduledDate <= new Date()) {
      setErrors((current) => ({ ...current, time: 'Choose a future date and time.' }));
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        interviewTitle: form.interviewTitle.trim(),
        interviewType: form.interviewType,
        platform: form.platform.trim() || null,
        meetingLink: form.interviewType === 'video_call' ? form.meetingLink.trim() : (form.meetingLink.trim() || null),
        interviewLocation: form.interviewType === 'in_person' ? form.interviewLocation.trim() : (form.interviewLocation.trim() || null),
        scheduledDate: scheduledDate.toISOString(),
        durationMinutes: Number(form.durationMinutes),
        timezone: form.timezone || 'UTC',
        interviewerEmails: emailList(form.interviewerEmails),
        ccEmails: emailList(form.ccEmails),
        candidateMessage: form.candidateMessage.trim() || null,
        instructions: form.instructions.trim() || null,
        notes: form.notes.trim() || null
      };

      await interviewAPI.scheduleForMatch(selectedMatch.jobId, selectedMatch.matchId, payload);
      toast.success('Interview invitation sent.');
      navigate('/interviews');
    } catch (error) {
      toast.error(extractError(error, 'Failed to schedule interview'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <div>
          <nav className="mb-4 flex items-center gap-2 text-sm text-slate-500">
            <button onClick={() => navigate('/interviews')} className="inline-flex items-center gap-1 hover:text-[#0b1f3a]">
              <PiArrowLeftLight size={12} /> Interviews
            </button>
            <PiCaretRightLight size={12} />
            <span className="font-medium text-[#0b1f3a]">Schedule</span>
          </nav>

          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.16em] text-sky-700">Interview invitation</p>
              <h1 className="mt-2 font-display text-3xl font-normal tracking-tight text-[#0b1f3a]">Schedule Interview</h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                Invite candidates only after both sides have shown interest. The invitation stores date, format, interviewers, candidate message, and instructions.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
          <div className="space-y-6">
            <Panel title="Candidate" icon={PiUserLight}>
              <div className="mb-4 flex flex-col gap-3 sm:flex-row">
                <div className="relative min-w-0 flex-1">
                  <PiMagnifyingGlassLight className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
                  <input value={search} onChange={(event) => setSearch(event.target.value)} className={`${inputClass} w-full pl-9`} placeholder="Search candidates ready for interview" />
                </div>
              </div>

              {errors.matchId && (
                <div className="mb-3 flex items-start gap-2 rounded-lg border border-rose-100 bg-rose-50 p-3 text-sm text-rose-700">
                  <PiWarningCircleLight size={13} className="mt-0.5 shrink-0" /> {errors.matchId}
                </div>
              )}

              {loadingMatches ? (
                <div className="flex min-h-[220px] items-center justify-center text-sm text-slate-500">
                  <PiCircleNotchLight className="mr-2 animate-spin" size={14} /> Loading matches
                </div>
              ) : filteredMatches.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center">
                  <PiUsersLight className="mx-auto text-slate-300" size={22} />
                  <h2 className="mt-3 font-display text-lg font-normal text-[#0b1f3a]">No candidates ready for interview</h2>
                  <p className="mt-1 text-sm text-slate-500">Express interest first, then wait for the candidate to accept.</p>
                  <button type="button" onClick={() => navigate('/candidates')} className="mt-4 h-10 rounded-lg bg-[#0b1f3a] px-4 text-sm font-medium text-white">
                    Open candidates
                  </button>
                </div>
              ) : (
                <div className="grid gap-3 lg:grid-cols-2">
                  {filteredMatches.map((match) => (
                    <CandidateOption
                      key={match.matchId}
                      match={match}
                      selected={String(form.matchId) === String(match.matchId)}
                      onSelect={(matchId) => update('matchId', String(matchId))}
                    />
                  ))}
                </div>
              )}
            </Panel>

            <Panel title="Interview Details" icon={PiCalendarCheckLight}>
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Title" error={errors.interviewTitle}>
                  <input value={form.interviewTitle} onChange={(event) => update('interviewTitle', event.target.value)} className={`${inputClass} w-full`} />
                </Field>
                <Field label="Timezone">
                  <input value={form.timezone} onChange={(event) => update('timezone', event.target.value)} className={`${inputClass} w-full`} />
                </Field>
                <Field label="Date" error={errors.date}>
                  <input type="date" value={form.date} onChange={(event) => update('date', event.target.value)} className={`${inputClass} w-full`} />
                </Field>
                <Field label="Time" error={errors.time}>
                  <input type="time" value={form.time} onChange={(event) => update('time', event.target.value)} className={`${inputClass} w-full`} />
                </Field>
                <Field label="Duration">
                  <select value={form.durationMinutes} onChange={(event) => update('durationMinutes', Number(event.target.value))} className={`${inputClass} w-full`}>
                    {[15, 30, 45, 60, 90, 120].map((minutes) => <option key={minutes} value={minutes}>{minutes} minutes</option>)}
                  </select>
                </Field>
                <Field label="Platform">
                  <input value={form.platform} onChange={(event) => update('platform', event.target.value)} className={`${inputClass} w-full`} />
                </Field>
              </div>

              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                {interviewTypes.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => update('interviewType', type.value)}
                      className={`flex h-20 flex-col items-center justify-center gap-2 rounded-lg border text-sm font-medium transition ${form.interviewType === type.value ? 'border-sky-300 bg-sky-50 text-sky-700' : 'border-slate-200 bg-white text-slate-600 hover:border-sky-200'}`}
                    >
                      <Icon size={26} className="mb-1" />
                      {type.label}
                    </button>
                  );
                })}
              </div>

              <div className="mt-5 grid gap-4 md:grid-cols-2">
                <Field label="Meeting link" error={errors.meetingLink}>
                  <input value={form.meetingLink} onChange={(event) => update('meetingLink', event.target.value)} className={`${inputClass} w-full`} placeholder="https://meet.example.com/..." />
                </Field>
                <Field label="Location" error={errors.interviewLocation}>
                  <input value={form.interviewLocation} onChange={(event) => update('interviewLocation', event.target.value)} className={`${inputClass} w-full`} placeholder="Office, room, address" />
                </Field>
              </div>
            </Panel>

            <Panel title="People And Notes" icon={PiEnvelopeSimpleLight}>
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Interviewer emails" error={errors.interviewerEmails}>
                  <input value={form.interviewerEmails} onChange={(event) => update('interviewerEmails', event.target.value)} className={`${inputClass} w-full`} placeholder="name@company.com, lead@company.com" />
                </Field>
                <Field label="CC emails" error={errors.ccEmails}>
                  <input value={form.ccEmails} onChange={(event) => update('ccEmails', event.target.value)} className={`${inputClass} w-full`} />
                </Field>
                <Field label="Candidate message">
                  <textarea value={form.candidateMessage} onChange={(event) => update('candidateMessage', event.target.value)} className={textareaClass} />
                </Field>
                <Field label="Instructions">
                  <textarea value={form.instructions} onChange={(event) => update('instructions', event.target.value)} className={textareaClass} />
                </Field>
                <div className="md:col-span-2">
                  <Field label="Internal notes">
                    <textarea value={form.notes} onChange={(event) => update('notes', event.target.value)} className={textareaClass} />
                  </Field>
                </div>
              </div>
            </Panel>
          </div>

          <aside className="space-y-6">
            <div className="sticky top-6 space-y-6">
              <Panel title="Summary" icon={PiChatCircleLight}>
                {selectedMatch ? (
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#0b1f3a] text-xs font-medium text-white">
                        {initialsFor(selectedMatch.candidate?.fullName)}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-[#0b1f3a]">{selectedMatch.candidate?.fullName}</div>
                        <div className="mt-1 text-xs text-slate-500">{selectedMatch.job?.title}</div>
                      </div>
                    </div>
                    <div className="grid gap-3">
                      <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                        <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">When</div>
                        <div className="mt-1 text-sm font-medium text-[#0b1f3a]">{form.date && form.time ? `${form.date} at ${form.time}` : 'Date and time not set'}</div>
                      </div>
                      <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                        <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">Format</div>
                        <div className="mt-1 text-sm font-medium text-[#0b1f3a]">{labelize(form.interviewType)} · {form.durationMinutes} min</div>
                      </div>
                      <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                        <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">Match</div>
                        <div className="mt-1 text-sm font-medium text-[#0b1f3a]">Band {selectedMatch.matchBand} · {Math.round(Number(selectedMatch.overallMatchScore || 0))}%</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                    Select a candidate.
                  </div>
                )}
              </Panel>

              <div className="flex flex-col gap-3">
                <button
                  type="submit"
                  disabled={submitting || loadingMatches}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-[#0b1f3a] px-4 text-sm font-medium text-white transition hover:bg-[#12345c] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? <PiCircleNotchLight className="animate-spin" size={13} /> : <PiArrowRightLight size={13} />}
                  Send invitation
                </button>
                <button type="button" onClick={() => navigate('/interviews')} className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 transition hover:bg-slate-50">
                  Cancel
                </button>
              </div>
            </div>
          </aside>
        </form>
      </div>
    </div>
  );
};

export default ScheduleInterviewPage;
